import React, { useState, useEffect, useRef } from 'react';
import Helmet from 'react-helmet';
import { CSSTransition } from 'react-transition-group';
import { addLoopedFocus } from '../../../util/accessibility';
import SafetyList from '../SafetyList';
import * as s from './styles';
const HASH_TRIGGER = '#ssi-safety';


/**
* Deep-link-only SSI overlay for /#ssi-safety.
* Tray clicks continue to use IndicationAndSafetyDrawer.
*/
const MultibrandSSIPopup = ({ location }) => {
 const safetyRef = useRef(null);
 const drawerScrollAreaRef = useRef(null);
 const hasCheckedHash = useRef(false);
 const [isDeepLink, setIsDeepLink] = useState(false);
 const [safetyDrawerOpen, setSafetyDrawerOpen] = useState(false);
 const [overrideCcpaStyle, setOverrideCcpaStyle] = useState(false);


 const resetDrawerScrollToTop = () => {
   const scrollEl = drawerScrollAreaRef.current;
   if (scrollEl) {
     scrollEl.scrollTo({ top: 0, behavior: 'auto' });
   }
 };


 const hideCcpaBannerIfPresent = () => {
   const ccpaBanner = document.querySelector('#onetrust-banner-sdk');
   if (ccpaBanner) {
     ccpaBanner.style.zIndex = '-1';
     setOverrideCcpaStyle(true);
   }
 };


 const showCcpaBanner = () => {
   const ccpaBanner = document.querySelector('#onetrust-banner-sdk');
   if (ccpaBanner && overrideCcpaStyle) {
     ccpaBanner.style.zIndex = '2147483645';
     setOverrideCcpaStyle(false);
   }
 };


 // Drawer Toggle Controls
 //----
 const openSafety = () => {
   hideCcpaBannerIfPresent();
   setSafetyDrawerOpen(true);


   if (safetyRef.current) {
     addLoopedFocus(safetyRef.current);
   }
 };

 const closeDrawers = () => {
   setSafetyDrawerOpen(false);
   showCcpaBanner();
   resetDrawerScrollToTop();
 };


 const handleKeyUp = (event) => {
   if (event.key === 'Escape' || event.keyCode === 27) {
     closeDrawers();
   }
 };


 // Direct load of /#ssi-safety only — mount first (in=false), then open
 // so CSSTransition can run the same 400ms enter animation as other drawers.
 //----
 useEffect(() => {
   if (hasCheckedHash.current) return;
   hasCheckedHash.current = true;


   if (location?.hash?.includes(HASH_TRIGGER)) {
     setIsDeepLink(true);
     window.scrollTo({
       top: 0,
       behavior: 'smooth',
     });


     const openTimer = window.setTimeout(() => {
       openSafety();
     }, 50);


     return () => window.clearTimeout(openTimer);
   }
   // eslint-disable-next-line react-hooks/exhaustive-deps
 }, []);


 useEffect(() => {
   if (!safetyDrawerOpen) {
     resetDrawerScrollToTop();
     return;
   }


   resetDrawerScrollToTop();
   const t1 = window.setTimeout(resetDrawerScrollToTop, 50);
   const t2 = window.setTimeout(resetDrawerScrollToTop, 420);


   return () => {
     window.clearTimeout(t1);
     window.clearTimeout(t2);
   };
 }, [safetyDrawerOpen]);


 useEffect(() => {
   if (!isDeepLink) return;


   document.addEventListener('keyup', handleKeyUp);
   window.addEventListener('resize', () => {
     if (safetyDrawerOpen) {
       hideCcpaBannerIfPresent();
     }
   });


   return () => {
     document.removeEventListener('keyup', handleKeyUp);
     window.removeEventListener('resize', () => {
       if (safetyDrawerOpen) {
         hideCcpaBannerIfPresent();
       }
     });
   };
 });
 //----


 if (!isDeepLink) {
   return null;
 }


 return (
   <>
     {safetyDrawerOpen &&
       <Helmet>
         <html lang="en-us" className="no-scroll" />
       </Helmet>
     }


     <CSSTransition
       in={safetyDrawerOpen}
       timeout={400}
       classNames="information-drawer"
     >
       <s.InformationDrawer
         isOpen={safetyDrawerOpen}
         drawerName="safety"
         ref={safetyRef}
       >
         <div className="container">
           <s.DrawerTopBar>
             <s.ModalButtonWrapper>
               <s.CloseButton
                 onClick={closeDrawers}
                 type="button"
                 aria-label="close"
               >
                 <s.CloseIcon aria-hidden="true" />
                 CLOSE
               </s.CloseButton>
             </s.ModalButtonWrapper>
           </s.DrawerTopBar>


           {/* <s.DrawerBannerText>
             Scroll for Selected Safety Information and Indications
           </s.DrawerBannerText> */}


           <s.DrawerScrollArea ref={drawerScrollAreaRef}>
             <SafetyList showSsiJobCode={true} />
           </s.DrawerScrollArea>
         </div>
       </s.InformationDrawer>
     </CSSTransition>


     <s.DrawerOverlay
       isActive={safetyDrawerOpen}
       onClick={closeDrawers}
     />
   </>
 );
};

export default MultibrandSSIPopup;
